// SPDX-License-Identifier: MIT
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';

export interface DropZoneFile {
  id: string;
  file: File;
}

export type RejectionReason = 'type' | 'size' | 'count' | 'empty';

export interface DropZoneOptions {
  onSubmit?: (files: DropZoneFile[]) => void | Promise<void>;
  // Fires per file the moment it passes validation — before any submit
  // button. Used by callers that want incremental upload (drop one file
  // → upload starts immediately) instead of the batch-submit pattern.
  // Multiple files dropped at once produce multiple onAdd calls.
  onAdd?: (file: DropZoneFile) => void;
  acceptedExtensions?: readonly string[];
  maxFileSize?: number;
  maxFiles?: number;
  // Test injection.
  uuid?: () => string;
}

export const DEFAULT_ACCEPTED: readonly string[] = [
  '.md', '.txt', '.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.json', '.yml', '.yaml', '.csv',
  '.docx', '.doc', '.odt', '.rtf',
  '.xlsx', '.xls', '.ods',
  '.html', '.htm',
] as const;
export const DEFAULT_MAX_SIZE = 25 * 1024 * 1024;
export const DEFAULT_MAX_FILES = 5;
export const PASTED_TEXT_MIN_LEN = 50;

export function renderDropZone(opts: DropZoneOptions = {}): HTMLElement {
  const accepted = opts.acceptedExtensions ?? DEFAULT_ACCEPTED;
  const maxSize = opts.maxFileSize ?? DEFAULT_MAX_SIZE;
  const maxFiles = opts.maxFiles ?? DEFAULT_MAX_FILES;
  const newId = opts.uuid ?? defaultUuid;

  const root = document.createElement('div');
  root.className = 'drop-zone';
  root.setAttribute('data-component', 'drop-zone');
  root.setAttribute('data-state', 'default');
  root.setAttribute('role', 'button');
  root.setAttribute('tabindex', '0');
  root.setAttribute('aria-label', t('dropZone.ariaLabel'));
  root.innerHTML = buildMarkup(accepted);

  const input = root.querySelector<HTMLInputElement>('[data-component="drop-zone-file-input"]')!;
  const folderInput = root.querySelector<HTMLInputElement>('[data-component="drop-zone-folder-input"]')!;
  folderInput.setAttribute('webkitdirectory', ''); // non-standard attr → set in JS
  const folderBtn = root.querySelector<HTMLButtonElement>('[data-component="drop-zone-folder-btn"]')!;
  const list = root.querySelector<HTMLUListElement>('[data-component="drop-zone-file-list"]')!;
  const empty = root.querySelector<HTMLElement>('[data-component="drop-zone-empty"]')!;
  const submitBtn = root.querySelector<HTMLButtonElement>('[data-component="drop-zone-submit"]')!;

  // Internal state.
  const items: DropZoneFile[] = [];
  const rejections: Array<{ id: string; name: string; reason: RejectionReason }> = [];

  function rerenderList(): void {
    list.innerHTML = '';
    for (const item of items) list.appendChild(buildFileCard(item, () => removeFile(item.id)));
    for (const rej of rejections) list.appendChild(buildRejectionCard(rej));
    const hasContent = items.length > 0 || rejections.length > 0;
    empty.hidden = hasContent;
    submitBtn.disabled = items.length === 0;
    if (items.length === 0 && rejections.length === 0) root.setAttribute('data-state', 'default');
  }

  function removeFile(id: string): void {
    const idx = items.findIndex((i) => i.id === id);
    if (idx >= 0) items.splice(idx, 1);
    const ridx = rejections.findIndex((r) => r.id === id);
    if (ridx >= 0) rejections.splice(ridx, 1);
    rerenderList();
  }

  function addFiles(incoming: File[]): void {
    rejections.length = 0;
    for (const f of incoming) {
      const reason = validate(f, accepted, maxSize);
      if (reason) {
        rejections.push({ id: newId(), name: f.name || t('dropZone.unnamedFile'), reason });
        continue;
      }
      if (items.length >= maxFiles) {
        rejections.push({ id: newId(), name: f.name, reason: 'count' });
        continue;
      }
      const entry: DropZoneFile = { id: newId(), file: f };
      items.push(entry);
      opts.onAdd?.(entry);
    }
    rerenderList();
  }

  // Click on the zone (not on inputs/buttons) opens the picker.
  root.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-component="drop-zone-file-card"]')) return;
    if (target.closest('[data-component="drop-zone-submit"]')) return;
    if (target.closest('[data-component="drop-zone-folder-btn"]')) return;
    if (target === input) return;
    input.click();
  });

  // Keyboard a11y: Enter / Space opens the picker.
  root.addEventListener('keydown', (e) => {
    if (e.target !== root) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      input.click();
    }
  });

  input.addEventListener('change', () => {
    if (!input.files) return;
    addFiles(Array.from(input.files));
    input.value = ''; // allow re-selecting the same file later
  });

  // Folder pick → same pipeline. stopPropagation so the button click doesn't
  // also bubble to the root handler (which would open the single-file picker).
  folderBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    folderInput.click();
  });

  // 2026-05-28 : expose programmatic triggers + addFiles on the root so the
  // magnify composer (which hides the drop-zone's visible chrome via CSS)
  // can wire its own visible affords ["✍️ Écris", "📋 Colle un texte",
  // "📁 Glisse un fichier / dossier"] to the same pipeline. Without this
  // exposure, the affords were dead — the operator dogfood 2026-05-28
  // found they did nothing. See composer.ts for the wiring.
  (root as HTMLElement & { __triggerFilePicker?: () => void }).__triggerFilePicker = () => input.click();
  (root as HTMLElement & { __triggerFolderPicker?: () => void }).__triggerFolderPicker = () => folderInput.click();
  (root as HTMLElement & { __addFiles?: (files: File[]) => void }).__addFiles = (files: File[]) => addFiles(files);
  folderInput.addEventListener('change', () => {
    if (!folderInput.files) return;
    addFiles(Array.from(folderInput.files));
    folderInput.value = '';
  });

  // Drag + drop.
  root.addEventListener('dragenter', (e) => {
    e.preventDefault();
    root.setAttribute('data-state', 'drag-over');
  });
  root.addEventListener('dragover', (e) => {
    e.preventDefault();
    root.setAttribute('data-state', 'drag-over');
  });
  root.addEventListener('dragleave', (e) => {
    // Reset only when the cursor leaves the root's boundary entirely.
    // `e.target === root` falsely stays drag-over when the cursor exits via a child (icon/title/etc.).
    if (!root.contains(e.relatedTarget as Node | null)) {
      root.setAttribute('data-state', 'default');
    }
  });
  root.addEventListener('drop', (e) => {
    e.preventDefault();
    root.setAttribute('data-state', 'default');
    const dt = e.dataTransfer;
    if (!dt) return;
    addFiles(Array.from(dt.files));
  });

  // Page-level paste (image / file / long text becomes virtual file).
  const pasteHandler = (raw: Event) => {
    const e = raw as ClipboardEvent;
    const cd = e.clipboardData;
    if (!cd) return;
    const collected: File[] = [];
    for (const item of Array.from(cd.items)) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) collected.push(f);
      }
    }
    if (collected.length === 0) {
      const text = cd.getData('text/plain');
      if (text && text.length >= PASTED_TEXT_MIN_LEN) {
        const blob = new Blob([text], { type: 'text/plain' });
        // happy-dom Blob -> File requires manual construction
        const file = typeof File !== 'undefined'
          ? new File([blob], 'pasted-brief.md', { type: 'text/plain' })
          : (Object.assign(blob, { name: 'pasted-brief.md' }) as unknown as File);
        collected.push(file);
      }
    }
    if (collected.length > 0) addFiles(collected);
  };
  document.addEventListener('paste', pasteHandler);
  // Stash unsubscribe on the element for tests + manual teardown.
  (root as unknown as { __unsubscribePaste?: () => void }).__unsubscribePaste = () => {
    document.removeEventListener('paste', pasteHandler);
  };

  // Submit.
  submitBtn.addEventListener('click', async (e) => {
    e.stopPropagation();
    if (items.length === 0) return;
    root.setAttribute('data-state', 'uploading');
    try {
      await opts.onSubmit?.(items.slice());
      root.setAttribute('data-state', 'done');
    } catch (err) {
      console.warn('[drop-zone] onSubmit failed:', err);
      root.setAttribute('data-state', 'error');
    }
  });

  rerenderList();
  return root;
}

function defaultUuid(): string {
  // Crypto-aware fallback ; tests inject a deterministic uuid via opts.
  if (typeof globalThis !== 'undefined' && (globalThis as { crypto?: Crypto }).crypto?.randomUUID) {
    return (globalThis as { crypto: Crypto }).crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function validate(f: File, accepted: readonly string[], maxSize: number): RejectionReason | null {
  if (f.size === 0) return 'empty';
  if (f.size > maxSize) return 'size';
  const lower = (f.name || '').toLowerCase();
  const ok = accepted.some((ext) => lower.endsWith(ext));
  if (!ok) return 'type';
  return null;
}

function buildMarkup(accepted: readonly string[]): string {
  const acceptAttr = accepted.join(',');
  return `
    <div class="drop-zone__inner">
      <div class="drop-zone__icon" aria-hidden="true">⤓</div>
      <div class="drop-zone__prompt">
        <div class="drop-zone__title" data-component="drop-zone-title">${escapeHtml(t('dropZone.title'))}</div>
        <div class="drop-zone__hint" id="drop-zone-hint" data-component="drop-zone-hint">${escapeHtml(t('dropZone.hint'))}</div>
      </div>
      <input type="file" multiple accept="${escapeHtml(acceptAttr)}" data-component="drop-zone-file-input" class="drop-zone__file-input" hidden>
      <!-- Folder pick: webkitdirectory is set in JS (non-standard attr). Lets the
           user attach a whole local folder as context — its files route through
           the same validate/addFiles pipeline as drag-drop / single pick. -->
      <input type="file" multiple data-component="drop-zone-folder-input" class="drop-zone__file-input" hidden>
    </div>
    <ul class="drop-zone__file-list" data-component="drop-zone-file-list" aria-live="polite" aria-relevant="additions removals" aria-atomic="false"></ul>
    <div class="drop-zone__empty" data-component="drop-zone-empty">${escapeHtml(t('dropZone.empty'))}</div>
    <div class="drop-zone__actions">
      <button type="button" class="drop-zone__folder-btn" data-component="drop-zone-folder-btn">📁 ${escapeHtml(t('dropZone.addFolder'))}</button>
      <button type="button" class="drop-zone__submit" data-component="drop-zone-submit" disabled>${escapeHtml(t('dropZone.submit'))}</button>
    </div>
  `;
}

function buildFileCard(item: DropZoneFile, onRemove: () => void): HTMLElement {
  const li = document.createElement('li');
  li.className = 'drop-zone__file-card';
  li.setAttribute('data-component', 'drop-zone-file-card');
  li.setAttribute('data-file-id', item.id);
  li.innerHTML = `
    <span class="drop-zone__file-icon" aria-hidden="true">📄</span>
    <span class="drop-zone__file-name" data-component="drop-zone-file-name">${escapeHtml(item.file.name)}</span>
    <span class="drop-zone__file-size" data-component="drop-zone-file-size">${formatBytes(item.file.size)}</span>
    <button type="button" class="drop-zone__file-remove" data-component="drop-zone-file-remove" aria-label="${escapeHtml(t('dropZone.removeAria'))}">×</button>
  `;
  li.querySelector<HTMLButtonElement>('[data-component="drop-zone-file-remove"]')!.addEventListener('click', (e) => {
    e.stopPropagation();
    onRemove();
  });
  return li;
}

function buildRejectionCard(rej: { id: string; name: string; reason: RejectionReason }): HTMLElement {
  const li = document.createElement('li');
  li.className = 'drop-zone__file-card drop-zone__file-card--rejected';
  li.setAttribute('data-component', 'drop-zone-rejection');
  li.setAttribute('data-file-id', rej.id);
  li.setAttribute('data-reason', rej.reason);
  li.setAttribute('role', 'alert');
  const messageKey = `dropZone.rejection.${rej.reason}`;
  li.innerHTML = `
    <span class="drop-zone__file-icon" aria-hidden="true">⚠️</span>
    <span class="drop-zone__file-name">${escapeHtml(rej.name)}</span>
    <span class="drop-zone__file-rejection-msg" data-component="drop-zone-rejection-msg">${escapeHtml(t(messageKey))}</span>
  `;
  return li;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
